import React, { useState, useEffect } from 'react';

const Navbar = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const closeMenu = () => {
        setIsMenuOpen(false);
    };

    const scrollToSection = (sectionId) => {
        if (sectionId === 'services') {
            // Services is part of the animated scroll sequence
            // According to App.js transforms:
            // - Services slides into view at 15-20% of scroll progress
            // - Total scroll container height is 557vh (desktop), 620vh (medium), 700vh (mobile)
            // We want to scroll to 20% to show Services when card animations start

                        // Get viewport height and determine scroll container height
            const viewportHeight = window.innerHeight;
            const width = window.innerWidth;
            let scrollContainerHeight;
            
            if (width < 640) { // mobile
                scrollContainerHeight = viewportHeight * 7; // 700vh
            } else if (width < 768) { // medium
                scrollContainerHeight = viewportHeight * 6.2; // 620vh
            } else { // desktop
                scrollContainerHeight = viewportHeight * 5.57; // 557vh
            }
            
            // Calculate 30% of the scroll container height
            // This ensures we're well into the Services section with card animations visible
            const targetScroll = scrollContainerHeight * 0.30;
            window.scrollTo({ top: targetScroll, behavior: 'smooth' });
        } else if (sectionId === 'hero') {
            // Hero is at the top
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            // For other sections, use normal scrollIntoView
            const element = document.getElementById(sectionId);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
        closeMenu(); // Close mobile menu after clicking
    };

    // Prevent body scroll when menu is open
    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        // Cleanup function to reset overflow when component unmounts
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMenuOpen]);

    return (
        <>
            {/* Navbar */}
            <nav className="h-16 sm:h-20 lg:h-[100px] bg-[#e3daec] text-black px-4 sm:px-8 md:px-12 lg:px-20 py-3 sm:py-4 lg:py-5 relative" role="navigation">
                <div className="flex items-center justify-between h-full">
                    {/* Logo - ALWAYS visible */}
                    <div>
                        <button
                            onClick={() => scrollToSection('hero')}
                            className="text-xl sm:text-2xl lg:text-3xl font-cormorant-bold"
                        >
                            <img src="/logo-ktia.png" alt="logo" className='w-24 h-24 mt-2 xl:w-40 xl:h-40 object-contain' />
                        </button>
                    </div>

                    {/* Desktop Navigation - ONLY visible on sm+ screens */}
                    <ul className="hidden sm:flex items-center gap-4 sm:gap-8 lg:gap-16">
                        <li className="hidden md:block">
                            <button
                                onClick={() => scrollToSection('hero')}
                                className="font-nunito font-light hover:underline text-sm lg:text-base"
                            >
                                Home
                            </button>
                        </li>
                        <li className="hidden md:block">
                            <button
                                onClick={() => scrollToSection('about')}
                                className="font-nunito font-light hover:underline text-sm lg:text-base"
                            >
                                About
                            </button>
                        </li>
                        <li className="hidden md:block">
                            <button
                                onClick={() => scrollToSection('services')}
                                className="font-nunito font-light hover:underline text-sm lg:text-base"
                            >
                                Services
                            </button>
                        </li>
                        <li className="hidden md:block">
                            <button
                                onClick={() => scrollToSection('booking')}
                                className="font-nunito-sans text-xs sm:text-sm underline whitespace-nowrap"
                            >
                                START TODAY
                            </button>
                        </li>
                    </ul>

                    {/* Hamburger Button - ONLY visible on mobile */}
                    <button
                        className="md:hidden flex flex-col justify-center items-center p-2 focus:outline-none"
                        onClick={toggleMenu}
                        aria-label="Toggle navigation menu"
                    >
                        <span className={`block w-6 h-0.5 bg-black transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-1.5' : ''}`}></span>
                        <span className={`block w-6 h-0.5 bg-black my-1 transition-all duration-300 ${isMenuOpen ? 'opacity-0' : ''}`}></span>
                        <span className={`block w-6 h-0.5 bg-black transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
                    </button>
                </div>
            </nav>

            {/* Mobile Menu - Full Viewport Height */}
            {isMenuOpen && (
                <div className="fixed inset-0 top-16 sm:hidden bg-[#e3daec] z-40">
                    <div className="flex flex-col items-center pt-12 space-y-8">
                        <button
                            onClick={() => scrollToSection('hero')}
                            className={`font-nunito font-light text-2xl hover:underline transition-all duration-300 hover:text-gray-600 ${isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                            style={{ transitionDelay: '100ms' }}
                        >
                            Home
                        </button>
                        <button
                            onClick={() => scrollToSection('about')}
                            className={`font-nunito font-light text-2xl hover:underline transition-all duration-300 hover:text-gray-600 ${isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                            style={{ transitionDelay: '200ms' }}
                        >
                            About
                        </button>
                        <button
                            onClick={() => scrollToSection('services')}
                            className={`font-nunito font-light text-2xl hover:underline transition-all duration-300 hover:text-gray-600 ${isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                            style={{ transitionDelay: '300ms' }}
                        >
                            Services
                        </button>
                        <button
                            onClick={() => scrollToSection('booking')}
                            className={`font-nunito-sans text-2xl underline transition-all duration-300 hover:text-gray-600 ${isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                            style={{ transitionDelay: '400ms' }}
                        >
                            START TODAY
                        </button>
                    </div>

                    {/* Background overlay for closing */}
                    <div
                        className="absolute inset-0 -z-10"
                        onClick={closeMenu}
                    ></div>
                </div>
            )}
        </>
    );
};

export default Navbar;
